import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { habitService, Habit, HabitCompletion, RecordCompletionData, HabitScore } from '../../services/habitService';
import AddHabitModal from './AddHabitModal';
import CompletionValueModal from './CompletionValueModal';
import './HabitTracker.css';

interface HabitWithCompletions extends Habit {
  completions: { [date: string]: HabitCompletion | boolean };
  score?: HabitScore;
}

interface HabitTrackerProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const HabitTracker: React.FC<HabitTrackerProps> = ({ user, onClose, onToggleSidebar }) => {
  const navigate = useNavigate();
  const [habits, setHabits] = useState<HabitWithCompletions[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInactiveHabits, setShowInactiveHabits] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const optionsMenuRef = useRef<HTMLDivElement>(null);

  const [showAddHabitModal, setShowAddHabitModal] = useState(false);
  const [completionModalState, setCompletionModalState] = useState<{
    habit: Habit;
    date: string;
  } | null>(null);

  // Load habits from API
  useEffect(() => {
    if (user?.id) {
      loadUserHabits();
    }
  }, [user, showInactiveHabits]);

  // Handle click outside to close options menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (optionsMenuRef.current && !optionsMenuRef.current.contains(event.target as Node)) {
        setShowOptionsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadUserHabits = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const userId = user.id;
      
      // Get user habits and date range completions in parallel
      const weekDates = getCurrentWeekDates();
      const startDate = formatDate(weekDates[0]);
      const endDate = formatDate(weekDates[weekDates.length - 1]);
      
      const [habitsResponse, completionsResponse] = await Promise.all([
        habitService.getUserHabits(userId, { 
          is_active: showInactiveHabits ? undefined : true, 
          limit: 100 
        }),
        habitService.getCompletionsByDateRange(userId, startDate, endDate)
      ]);
      
      if (habitsResponse.success && habitsResponse.habits) {
        // Process completions data
        const completionsByDate = completionsResponse.success && completionsResponse.completions_by_date 
          ? completionsResponse.completions_by_date 
          : {};
        
        // Debug: Log the completions data
        console.log('Completions by date:', completionsByDate);
        console.log('Completions response:', completionsResponse);
        console.log('Week dates:', weekDates.map(formatDate));
        console.log('Available habits:', habitsResponse.habits.map(h => ({ id: h.id, name: h.name })));
        
        // Map habits to their completions
        const habitsWithCompletions = habitsResponse.habits.map((habit) => {
          const completions: { [date: string]: HabitCompletion | boolean } = {};
          
          weekDates.forEach((date) => {
            const dateStr = formatDate(date);
            const dayCompletions = completionsByDate[dateStr] || [];
            // Convert habit_id to number for comparison since API might return strings
            const habitCompletion = dayCompletions.find(c => Number(c.habit_id) === Number(habit.id));
            
            // Debug logging for each date
            console.log(`Processing habit ${habit.name} for date ${dateStr}:`, {
              dayCompletions: dayCompletions.length,
              dayCompletionsData: dayCompletions,
              habitCompletion: habitCompletion ? 'found' : 'not found',
              habitId: habit.id,
              lookingFor: Number(habit.id)
            });
            
            if (habitCompletion) {
              // Ensure is_completed is properly converted to boolean
              const normalizedCompletion = {
                ...habitCompletion,
                is_completed: Boolean(habitCompletion.is_completed),
                target_achieved: Boolean(habitCompletion.target_achieved)
              };
              completions[dateStr] = normalizedCompletion;
            } else {
              completions[dateStr] = false;
            }
          });
          
          return {
            ...habit,
            completions
          } as HabitWithCompletions;
        });
        
        // Fetch scores for all habits
        await loadHabitScores(habitsWithCompletions, userId);
      } else {
        setError(habitsResponse.message || 'Failed to load habits');
      }
    } catch (err) {
      console.error('Error loading habits:', err);
      setError('Failed to load habits. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadHabitScores = async (habits: HabitWithCompletions[], userId: number) => {
    try {
      // Fetch scores for all habits in parallel
      const scorePromises = habits.map(async (habit) => {
        try {
          const scoreResponse = await habitService.getHabitScore(habit.id, userId, { days: 30 });
          if (scoreResponse.success && scoreResponse.score) {
            return { habitId: habit.id, score: scoreResponse.score };
          }
          return { habitId: habit.id, score: null };
        } catch (error) {
          console.error(`Error fetching score for habit ${habit.id}:`, error);
          return { habitId: habit.id, score: null };
        }
      });

      const scoreResults = await Promise.all(scorePromises);
      
      // Update habits with their scores
      const habitsWithScores = habits.map(habit => {
        const scoreResult = scoreResults.find(result => result.habitId === habit.id);
        return {
          ...habit,
          score: scoreResult?.score || undefined
        };
      });
      
      setHabits(habitsWithScores);
    } catch (error) {
      console.error('Error loading habit scores:', error);
      // Still set habits even if scores fail
      setHabits(habits);
    }
  };

  // Get 4 days ending with today
  const getCurrentWeekDates = () => {
    const today = new Date();
    const dates = [];
    
    // Get 3 days before today + today (4 days total)
    for (let i = 3; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      dates.push(date);
    }
    
    return dates;
  };

  const weekDates = getCurrentWeekDates();
  const weekDays = weekDates.map(date => {
    const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
    return dayNames[date.getDay()];
  });

  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const formatDisplayDate = (date: Date) => {
    return date.getDate().toString();
  };

  const toggleHabitCompletion = async (habitId: number, date: string) => {
    if (!user?.id) return;

    const habit = habits.find(h => h.id === habitId);
    if (!habit) return;

    // For numeric tracking types, open the modal instead of toggling
    if (habit.tracking_type === 'count' || habit.tracking_type === 'measurement' || habit.tracking_type === 'both') {
      setCompletionModalState({ habit, date });
      return;
    }

    // For boolean tracking, toggle as before
    try {
      const userId = user.id;
      const currentCompletion = habit.completions[date];
      const isCurrentlyCompleted = typeof currentCompletion === 'object' 
        ? Boolean(currentCompletion.is_completed)
        : Boolean(currentCompletion);
      
      const completionData: RecordCompletionData = {
        habit_id: habitId,
        user_id: userId,
        completion_date: date,
        is_completed: !isCurrentlyCompleted
      };
      
      const response = await habitService.recordCompletion(completionData);
      
      if (response.success) {
        // Update local state optimistically
        setHabits(prevHabits => 
          prevHabits.map(h => 
            h.id === habitId 
              ? {
                  ...h,
                  completions: {
                    ...h.completions,
                    [date]: {
                      habit_id: habitId,
                      completion_date: date,
                      is_completed: !isCurrentlyCompleted,
                      target_achieved: response.target_achieved || false,
                      achievement_percentage: response.achievement_percentage || 0
                    } as HabitCompletion
                  }
                }
              : h
          )
        );
      } else {
        setError(response.message || 'Failed to update habit completion');
      }
    } catch (err) {
      console.error('Error toggling habit completion:', err);
      setError('Failed to update habit completion. Please try again.');
    }
  };

  const handleSaveCompletionValue = async (count: number | undefined, measurement: number | undefined) => {
    if (!user?.id || !completionModalState) return;

    try {
      const userId = user.id;
      const { habit, date } = completionModalState;
      
      const completionData: RecordCompletionData = {
        habit_id: habit.id,
        user_id: userId,
        completion_date: date,
        is_completed: count !== undefined || measurement !== undefined,
        completion_count: count,
        measurement_value: measurement
      };
      
      const response = await habitService.recordCompletion(completionData);
      
      if (response.success) {
        // Update local state optimistically
        setHabits(prevHabits => 
          prevHabits.map(h => 
            h.id === habit.id 
              ? {
                  ...h,
                  completions: {
                    ...h.completions,
                    [date]: {
                      habit_id: habit.id,
                      completion_date: date,
                      is_completed: count !== undefined || measurement !== undefined,
                      completion_count: count,
                      measurement_value: measurement,
                      target_achieved: response.target_achieved || false,
                      achievement_percentage: response.achievement_percentage || 0
                    } as HabitCompletion
                  }
                }
              : h
          )
        );
        setCompletionModalState(null);
      } else {
        setError(response.message || 'Failed to update habit completion');
      }
    } catch (err) {
      console.error('Error saving completion value:', err);
      setError('Failed to save completion value. Please try again.');
    }
  };

  const handleHabitAdded = async () => {
    await loadUserHabits();
  };

  const deleteHabit = async (habitId: number) => {
    if (!window.confirm('Are you sure you want to delete this habit?')) return;
    
    try {
      const response = await habitService.deleteHabit(habitId);
      
      if (response.success) {
        // Remove from local state
        setHabits(prev => prev.filter(habit => habit.id !== habitId));
      } else {
        setError(response.message || 'Failed to delete habit');
      }
    } catch (err) {
      console.error('Error deleting habit:', err);
      setError('Failed to delete habit. Please try again.');
    }
  };

  // Progress line component
  const ProgressLine: React.FC<{ score?: HabitScore }> = ({ score }) => {
    if (!score) {
      return <div className="progress-line-fill" style={{ width: '0%' }}></div>;
    }

    // Convert 0-1 score to percentage (0-100)
    const percentage = Math.round(score.score * 100);
    const getScoreColor = (scoreValue: number) => {
      // scoreValue is now between 0-1, so we need to convert to 0-100 for comparison
      const percentageValue = scoreValue * 100;
      if (percentageValue >= 80) return '#4CAF50'; // Green
      if (percentageValue >= 60) return '#FF9800'; // Orange
      if (percentageValue >= 40) return '#FFC107'; // Yellow
      return '#F44336'; // Red
    };

    return (
      <div 
        className="progress-line-fill" 
        style={{ 
          width: `${percentage}%`,
          backgroundColor: getScoreColor(score.score)
        }}
      ></div>
    );
  };

  if (loading) {
    return (
      <div className="habit-tracker">
              <header className="habit-tracker-header">
        <div className="header-left">
          <button className="menu-btn" onClick={onToggleSidebar}>☰</button>
          <button className="back-btn" onClick={onClose}>
            ←
          </button>
        </div>
        <h1>Habits</h1>
      </header>
        <div className="habit-tracker-content">
          <div className="loading-state">
            <p>Loading habits...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="habit-tracker">
      <header className="habit-tracker-header">
        <div className="header-left">
          <button className="menu-btn" onClick={onToggleSidebar}>☰</button>
        </div>
        <h1>Habits</h1>
        <div className="header-right">
          <button className="add-habit-btn" onClick={() => setShowAddHabitModal(true)}>
            +
          </button>
          <div className="options-menu-container" ref={optionsMenuRef}>
            <button 
              className="options-btn"
              onClick={() => setShowOptionsMenu(!showOptionsMenu)}
            >
              ⋮
            </button>
            {showOptionsMenu && (
              <div className="options-menu-popover">
                <div className="options-menu-item">
                  <label className="options-menu-toggle">
                    <input
                      type="checkbox"
                      checked={showInactiveHabits}
                      onChange={(e) => setShowInactiveHabits(e.target.checked)}
                    />
                    <span className="options-menu-label">Show inactive habits</span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {error && (
        <div className="error-message" style={{ padding: '10px', backgroundColor: '#fee', color: '#c00', margin: '10px' }}>
          {error}
          <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        </div>
      )}

      <div className="habit-tracker-content">
        {/* Week Header */}
        <div className="week-header">
          <div className="habit-info-placeholder"></div>
          {weekDates.map((date, index) => (
            <div key={index} className="day-column">
              <div className="day-label">{weekDays[index]}</div>
              <div className="day-number">{formatDisplayDate(date)}</div>
            </div>
          ))}
        </div>

        {/* Habits List */}
        <div className="habits-list">
          {habits.map((habit) => (
            <div key={habit.id} className={`habit-row ${!habit.is_active ? 'inactive' : ''}`}>
              <div className="habit-info">
                <span className="habit-icon">{habit.icon}</span>
                <span 
                  className="habit-name clickable"
                  onClick={() => navigate(`/habits/${habit.id}`)}
                  title="View habit details"
                >
                  {habit.name}
                  {!habit.is_active && <span className="inactive-label">Inactive</span>}
                </span>
                <button 
                  className="delete-habit-btn"
                  onClick={() => deleteHabit(habit.id)}
                  title="Delete habit"
                >
                  ×
                </button>
              </div>
              <div className="habit-completions">
                {weekDates.map((date, index) => {
                  const dateStr = formatDate(date);
                  const completion = habit.completions[dateStr];
                  const isCompleted = typeof completion === 'object' 
                    ? Boolean(completion.is_completed)
                    : Boolean(completion);
                  
                  // Get numeric values if present
                  const completionObj = typeof completion === 'object' ? completion : null;
                  const countValue = completionObj?.completion_count;
                  const measurementValue = completionObj?.measurement_value;
                  
                  // Determine what to display
                  const isNumericTracking = habit.tracking_type === 'count' || 
                                           habit.tracking_type === 'measurement' || 
                                           habit.tracking_type === 'both';
                  
                  let displayText = '';
                  if (isNumericTracking && isCompleted) {
                    if (habit.tracking_type === 'count' && countValue !== undefined) {
                      displayText = countValue.toString();
                    } else if (habit.tracking_type === 'measurement' && measurementValue !== undefined) {
                      displayText = measurementValue.toString();
                    } else if (habit.tracking_type === 'both') {
                      const parts = [];
                      if (countValue !== undefined) parts.push(countValue.toString());
                      if (measurementValue !== undefined) {
                        parts.push(`${measurementValue}${habit.measurement_unit || ''}`);
                      }
                      displayText = parts.join(' / ');
                    }
                  } else {
                    displayText = isCompleted ? '✓' : '×';
                  }
                  
                  return (
                    <button
                      key={index}
                      className={`completion-btn ${isCompleted ? 'completed' : ''} ${isNumericTracking ? 'numeric' : ''}`}
                      onClick={() => toggleHabitCompletion(habit.id, dateStr)}
                      title={isNumericTracking && isCompleted ? `${habit.name} - ${displayText}` : undefined}
                    >
                      {displayText}
                    </button>
                  );
                })}
              </div>
              {/* Progress line at the bottom */}
              <div className="habit-progress-line">
                <ProgressLine score={habit.score} />
              </div>
            </div>
          ))}
        </div>

        {habits.length === 0 && (
          <div className="empty-state">
            <p>No habits yet. Tap + to add your first habit!</p>
          </div>
        )}
      </div>

      {/* Add Habit Modal */}
      {showAddHabitModal && user && (
        <AddHabitModal
          userId={user.id}
          onClose={() => setShowAddHabitModal(false)}
          onHabitAdded={handleHabitAdded}
          onError={setError}
        />
      )}

      {/* Completion Value Modal */}
      {completionModalState && (() => {
        const habitWithCompletions = habits.find(h => h.id === completionModalState.habit.id);
        const completion = habitWithCompletions?.completions[completionModalState.date];
        return (
          <CompletionValueModal
            habit={completionModalState.habit}
            date={completionModalState.date}
            currentValue={typeof completion === 'object' ? completion.completion_count : undefined}
            currentMeasurement={typeof completion === 'object' ? completion.measurement_value : undefined}
            onSave={handleSaveCompletionValue}
            onClose={() => setCompletionModalState(null)}
          />
        );
      })()}
    </div>
  );
};

export default HabitTracker;
